import React, { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { useLocation, useHistory } from 'react-router-dom';
import Logo from 'components/icons/Logo';
import TwoLinesLabelledInput from 'components/TwoLinesLabelledInput';
import CheckBox from './components/CheckBox';
import useTransition from 'hooks/useTransition';
import useExternalAuthConfig from 'hooks/useExternalAuthConfig';
import { usePathData } from 'dataProvider/pathData';
import getAppAPIBase from 'utils/getAppAPIBase';
import {
    WORKOS_ERROR_MESSAGES_JP,
    WORKOS_ERROR_TITLE_JP,
    WORKOS_ERROR_FALLBACK_JP,
} from './constants/AuthErrorMessage';
import Google from 'components/icons/Google';
import Microsoft from 'components/icons/Microsoft';
import LoadingCircle from 'components/LoadingCircle';

export default function LoginPageLayout(props) {
    const { onConfirm, isAdmin = false, isSystemAdmin = false } = props;
    const location = useLocation();
    const history = useHistory();
    const { clientId } = usePathData();
    const { config: authConfig, loading: authConfigLoading } = useExternalAuthConfig(clientId);
    const didStripUrlRef = useRef(false);

    const [userData, setUserData] = useState({ id: '', password: '' });
    const [loginError, setLoginError] = useState(null);

    const handleInput = e => {
        setUserData({ ...userData, [e.target.name]: e.target.value });
    };

    const handleCheckbox = e => {
        setUserData({ ...userData, [e.target.name]: e.target.checked });
    };

    const handleClick = () => {
        onConfirm(userData);
    };

    const { pushHistoryAbs } = useTransition();
    const forgetPassword = () => {
        pushHistoryAbs('/password/send');
    };

    const appBase = clientId ? getAppAPIBase(clientId) : '';
    const showSso = authConfig?.sso_enabled && appBase;
    const showGoogle = authConfig?.google_login_enabled && appBase;
    const showMicrosoft = authConfig?.microsoft_login_enabled && appBase;
    const showPasswordLogin = isSystemAdmin ? true : authConfig?.password_login_enabled === true;
    const isAuthBootstrapping = !isSystemAdmin && !!clientId && authConfigLoading;
    const pageStyle = showPasswordLogin
        ? undefined
        : {
              paddingTop: 0,
              marginTop: '-72px',
              minHeight: 'calc(100vh + 72px)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
          };
    const callback = useRef(null);
    if (callback.current === null) {
        const searchParams = new URLSearchParams(location.search || '');
        const cb = searchParams.get('callback');
        callback.current = cb && typeof cb === 'string' && cb !== '' ? cb : '';
    }

    useEffect(() => {
        if (didStripUrlRef.current) return;
        const searchParams = new URLSearchParams(location.search || '');
        const authError = searchParams.get('auth_error');
        const authErrorDescription = searchParams.get('auth_error_description');
        if (authError) {
            didStripUrlRef.current = true;
            setLoginError({ code: authError, description: authErrorDescription || null });
            history.replace(location.pathname);
        }
    }, [location.pathname, location.search, history]);

    const errorTitle = WORKOS_ERROR_TITLE_JP;
    const errorMessage = loginError
        ? WORKOS_ERROR_MESSAGES_JP[loginError.code] || WORKOS_ERROR_FALLBACK_JP
        : null;

    return (
        <div className="page login-template" style={pageStyle}>
            <div className="card p-4">
                <Logo className="card-img-top logo icon-primary" />
                <h2 className="card-subtitle text-center h6">-あなたの本音を届けよう-</h2>
                <div className="card-body">
                    {isAuthBootstrapping ? (
                        <div className="d-flex flex-column align-items-center">
                            <LoadingCircle isLoading={true} className="mb-3" />
                            <div className="text-muted" style={{ fontSize: 12 }}>
                                ログイン設定を読み込み中です…
                            </div>
                        </div>
                    ) : (
                        <>
                            {!authConfigLoading && (showSso || showGoogle || showMicrosoft) && (
                                <div className="login-template__social-login">
                                    {loginError && errorMessage && (
                                        <div
                                            className="alert alert-danger d-flex align-items-start"
                                            role="alert"
                                            style={{ backgroundColor: '#f8d7da', margin: 0 }}
                                        >
                                            <div>
                                                <strong className="d-block text-danger">
                                                    {errorTitle}
                                                </strong>
                                                <span className="text-dark">{errorMessage}</span>
                                            </div>
                                        </div>
                                    )}
                                    {showGoogle && (
                                        <form method="POST" action={`${appBase}/auth/initiate`}>
                                            <input type="hidden" name="provider" value="google" />
                                            {callback.current && (
                                                <input
                                                    type="hidden"
                                                    name="callback"
                                                    value={callback.current}
                                                />
                                            )}
                                            <input
                                                type="hidden"
                                                name="is_admin"
                                                value={isAdmin ? '1' : '0'}
                                            />
                                            <button
                                                type="submit"
                                                className="btn login-template__auth-btn btn-lg btn-block"
                                            >
                                                <span
                                                    className="login-template__auth-btn-icon"
                                                    aria-hidden="true"
                                                >
                                                    <Google
                                                        style={{ width: '24px', height: '24px' }}
                                                    />
                                                </span>
                                                Googleでログイン
                                            </button>
                                        </form>
                                    )}
                                    {showMicrosoft && (
                                        <form
                                            method="POST"
                                            action={`${appBase}/auth/microsoft/initiate`}
                                        >
                                            {callback.current && (
                                                <input
                                                    type="hidden"
                                                    name="callback"
                                                    value={callback.current}
                                                />
                                            )}
                                            <input
                                                type="hidden"
                                                name="is_admin"
                                                value={isAdmin ? '1' : '0'}
                                            />
                                            <button
                                                type="submit"
                                                className="btn login-template__auth-btn btn-lg btn-block"
                                            >
                                                <span
                                                    className="login-template__auth-btn-icon"
                                                    aria-hidden="true"
                                                >
                                                    <Microsoft
                                                        style={{
                                                            width: '24px',
                                                            height: '24px',
                                                        }}
                                                    />
                                                </span>
                                                Microsoftでログイン
                                            </button>
                                        </form>
                                    )}
                                    {showSso && (
                                        <form method="POST" action={`${appBase}/auth/initiate`}>
                                            <input type="hidden" name="provider" value="sso" />
                                            {callback.current && (
                                                <input
                                                    type="hidden"
                                                    name="callback"
                                                    value={callback.current}
                                                />
                                            )}
                                            <input
                                                type="hidden"
                                                name="is_admin"
                                                value={isAdmin ? '1' : '0'}
                                            />
                                            <button
                                                type="submit"
                                                className="btn login-template__auth-btn btn-lg btn-block"
                                            >
                                                シングルサインオンを使用
                                            </button>
                                        </form>
                                    )}
                                    {showPasswordLogin && (
                                        <div
                                            className="text-center"
                                            style={{
                                                fontSize: '14px',
                                                color: '#252525',
                                                fontWeight: '400',
                                            }}
                                        >
                                            または
                                        </div>
                                    )}
                                </div>
                            )}

                            {showPasswordLogin && (
                                <>
                                    <TwoLinesLabelledInput
                                        label="ID（メールアドレス/電話番号）"
                                        name="id"
                                        value={userData.id}
                                        onChange={handleInput}
                                    />
                                    <TwoLinesLabelledInput
                                        label="パスワード"
                                        password
                                        name="password"
                                        value={userData.password}
                                        onChange={handleInput}
                                    />
                                    <p className="card-text mb-4 mt-1">
                                        <small>
                                            <u>
                                                <a
                                                    className="text-muted cursor-pointer"
                                                    onClick={forgetPassword}
                                                >
                                                    パスワードを忘れた方はこちら
                                                </a>
                                            </u>
                                        </small>
                                    </p>
                                    <button
                                        type="button"
                                        className="btn btn-secondary btn-lg btn-block mb-2"
                                        onClick={handleClick}
                                    >
                                        ログイン
                                    </button>
                                    <CheckBox
                                        name="remember"
                                        label="ログイン状態を維持する"
                                        id="keeplogincheck"
                                        onChange={handleCheckbox}
                                        value={1}
                                    />
                                </>
                            )}
                        </>
                    )}
                </div>
            </div>
            {showPasswordLogin && (
                <div className="card p-4 mt-4">
                    <div className="card-body">
                        <h2 className="card-subtitle h6">お知らせ</h2>
                    </div>
                </div>
            )}
        </div>
    );
}

LoginPageLayout.propTypes = {
    onConfirm: PropTypes.func.isRequired,
    isAdmin: PropTypes.bool,
    isSystemAdmin: PropTypes.bool,
};
